import React, { useCallback, useEffect } from 'react';
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  TextField,
  Button,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import DialogTransition from './Common/DialogTransition';
import REGEX from '../const/Regex';
import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';
import { DropDown, DropdownItem } from './Common/DropDown';

const MAPPED_FIELD_OPTIONS = [
  { label: 'Email', value: 'email' },
  { label: 'Last Name', value: 'family_name' },
  { label: 'First Name', value: 'given_name' },
  { label: 'Username', value: 'username' },
  { label: 'Active', value: 'is_active' },
];

/**
 * @component CreateEditIDPClaimMappingModal
 * @description CreateEditIDPClaimMappingModal - React component for creating/editing IDP claim mapping.
 * @param {Object} props - The props object containing the component's data and functions.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.onClose - Function to close the dialog.
 * @param {Function} props.onSave - Function to save the claim mapping.
 * @param {Object} props.data - Current mapping data for edit mode.
 * @param {string} props.mode - Mode of the dialog ('create' or 'edit').
 * @param {string} props.title - Title of the dialog.
 * @returns {JSX.Element} React component.
 */

const CreateEditIDPClaimMappingModal = ({
  open,
  onClose,
  onSave,
  data,
  mode = 'create',
  idpType,
  title,
}) => {
  const theme = useTheme();

  const initialValues = {
    claim_name: '',
    mapped_field: '',
  };

  /**
   * Validation schema for the form fields.
   * @typedef {Object} ValidationSchema
   * @property {string} claim_name - The claim name field validation rules.
   * @property {string} mapped_field - The mapped field validation rules.
   */

  const validationSchema = object({
    claim_name: string()
      .required('Required')
      .test('is-valid', "Claim name isn't a valid URL", function (value) {
        if (idpType === 'SAML') {
          return REGEX.URL.test(value || '');
        }
        return true;
      })
      .max(255, 'Claim name must be at most 255 characters'),
    mapped_field: string()
      .required('Required')
      .min(1, 'Mapped field must be at least 1 character')
      .max(255, 'Mapped field must be at most 255 characters'),
  });

  /**
   * Formik form object for managing form state and submission.
   * @typedef {Object} FormikForm
   * @property {Object} initialValues - Initial values for form fields.
   * @property {Object} validationSchema - Validation schema for form fields.
   * @property {Function} onSubmit - Function to handle form submission.
   * @property {Function} handleReset - Function to reset form fields.
   */

  const formikForm = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (formData) => {
      try {
        const payload = {
          claim_name: formData.claim_name,
          mapped_field: formData.mapped_field,
        };
        await onSave(payload);
      } catch (error) {
        console.error('Error saving claim mapping:', error);
      }
      formikForm.handleReset();
    },
  });

  // Populate form when in edit mode
  useEffect(() => {
    if (mode === 'edit' && data) {
      formikForm.setValues({
        claim_name: data.claim_name || '',
        mapped_field: data.mapped_field || '',
      });
    } else {
      formikForm.resetForm();
    }
  }, [mode, data, open]);

  const handleChange = useCallback(
    (e) => formikForm.handleChange(e),
    [formikForm]
  );

  const handleOnClose = useCallback(() => {
    onClose();
    formikForm.handleReset();
  }, [formikForm, onClose]);

  return (
    <Dialog
      maxWidth="xs"
      fullWidth={true}
      onClose={handleOnClose}
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>
        {title ||
          (mode === 'edit' ? 'Edit Claim Mapping' : 'Create Claim Mapping')}
      </DialogTitle>
      <IconButton
        size="large"
        onClick={handleOnClose}
        sx={{ position: 'absolute', right: 16, top: 8 }}
      >
        <CloseIcon style={{ color: theme.palette.other.icon }} />
      </IconButton>
      <DialogContent dividers sx={{ padding: '0px' }}>
        <Grid
          container
          direction="column"
          rowSpacing={2}
          padding={3}
          display="flex"
          gap={2}
        >
          <Grid item>
            <TextField
              required
              placeholder="Enter claim name"
              name="claim_name"
              label="Claim name"
              value={formikForm.values.claim_name}
              onChange={handleChange}
              aria-label="claim_name"
              onBlur={formikForm.handleBlur}
              helperText={
                formikForm?.errors?.claim_name &&
                formikForm?.touched?.claim_name
                  ? formikForm?.errors?.claim_name
                  : null
              }
              error={
                Boolean(formikForm?.errors?.claim_name) &&
                formikForm?.touched?.claim_name
              }
            />
          </Grid>
          <Grid item>
            <DropDown
              required
              label="Mapped field"
              placeholder="Enter mapped field"
              name="mapped_field"
              value={formikForm.values.mapped_field}
              onChange={handleChange}
              onBlur={formikForm.handleBlur}
              error={
                Boolean(formikForm?.errors?.mapped_field) &&
                formikForm?.touched?.mapped_field
              }
              helperText={
                formikForm?.errors?.mapped_field &&
                formikForm?.touched?.mapped_field
                  ? formikForm?.errors?.mapped_field
                  : null
              }
            >
              {MAPPED_FIELD_OPTIONS.map((option) => (
                <DropdownItem key={option.value} value={option.value}>
                  {option.label}
                </DropdownItem>
              ))}
            </DropDown>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={formikForm.handleReset} title="Reset">
          Reset
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={formikForm.handleSubmit}
          title="Submit"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditIDPClaimMappingModal;
